import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  programs,
  sites,
  WITHHELD_BECAUSE,
  WITHHELD_LABEL,
  withheldSentence,
} from '@cwos/api-client';
import type { programs as ProgramsNS } from '@cwos/api-client';
import {
  ConfirmAction,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  useTabListKeys,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { useNav } from '../navigation.js';
import { SettingsActions, SettingsCard, SettingsField, SettingsHeader } from './common.js';
import {
  canEdit,
  currentWorkers,
  pastWorkers,
  programDraftDirty,
  programDraftOf,
  programLeadLine,
  programUpdatePayload,
  rosterCell,
  statusBadge,
  supervisorsLine,
  validateProgramDraft,
} from './organizationLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import { FindingsPanel } from '../insights/FindingsPanel.js';

type Tab = 'overview' | 'people' | 'sites' | 'settings';

/**
 * A program as an operational object, not a form.
 *
 * Four tabs, each backed by a real API surface — nothing is shown that has no
 * endpoint behind it. Overview is derived from the same tables the dashboard
 * aggregates over, so a program's "hours worked" cannot disagree with the
 * organization's.
 *
 * The People tab keeps CURRENT and PAST membership visibly separate. That
 * distinction is the whole point of effective-dated assignments: someone who
 * left in June is not a current member, and June's work is still theirs.
 */
export function ProgramDetail() {
  const { programId } = useParams({ strict: false }) as { programId: string };
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManage = has('program:manage');
  const [tab, setTab] = useState<Tab>('overview');
  // Above the early returns below — a hook cannot sit behind a conditional.
  const programTabs = useTabListKeys<HTMLDivElement>();

  const detail = useQuery({
    queryKey: ['programs', 'detail', programId],
    queryFn: () => programs.detail(programId),
  });
  const program = detail.data;

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 1080 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }
  if (detail.isError || !program) {
    return (
      <div className="rn-col" style={{ maxWidth: 680 }}>
        <LoadFailure
          error={detail.error}
          mainRead
          message="We couldn’t load that program. It may have been removed, or it may belong to a different organization."
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const badge = statusBadge(program.status);
  const editable = canManage && canEdit(program.status);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'people', label: `People (${currentWorkers(program.workers).length})` },
    { key: 'sites', label: `Sites (${program.sites.length})` },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <div className="org-detail-head">
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => navigate('/settings/programs')}
        >
          ← All programs
        </button>
        <SettingsHeader title={program.name} blurb={program.description ?? undefined} />
        <div className="org-detail-meta">
          <Pill variant={badge.variant} dot>
            {badge.label}
          </Pill>
          <span className="rn-muted">{badge.meaning}</span>
          {program.code ? <span className="rn-muted">Code: {program.code}</span> : null}
        </div>
      </div>

      {/* This strip already carried the ARIA a tab strip owes: ids,
          aria-controls, and a real tabpanel. What it did not have was the
          keyboard behaviour the role promises — arrows to move, one tab stop
          for the whole strip. `useTabListKeys` supplies both. */}
      <div className="org-tabs" role="tablist" aria-label="Program sections" {...programTabs}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`program-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls={`program-panel-${t.key}`}
            className={`org-tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`program-panel-${tab}`}
        aria-labelledby={`program-tab-${tab}`}
        className="rn-col"
      >
        {tab === 'overview' ? <Overview program={program} /> : null}
        {tab === 'people' ? (
          <People
            program={program}
            canManage={editable}
            onChanged={() => invalidate(qc, programId)}
          />
        ) : null}
        {tab === 'sites' ? (
          <Sites
            program={program}
            canManage={editable}
            onChanged={() => invalidate(qc, programId)}
          />
        ) : null}
        {tab === 'settings' ? (
          <ProgramSettingsTab
            program={program}
            canManage={canManage}
            onChanged={() => invalidate(qc, programId)}
            onDeleted={() => navigate('/settings/programs')}
          />
        ) : null}
      </div>
    </div>
  );
}

function invalidate(qc: ReturnType<typeof useQueryClient>, programId: string): void {
  void qc.invalidateQueries({ queryKey: ['programs'] });
  void qc.invalidateQueries({ queryKey: ['programs', 'detail', programId] });
  // A program's site set changes what the Sites screen shows, so its cache goes
  // too — the two surfaces edit one relationship and must never diverge.
  void qc.invalidateQueries({ queryKey: ['sites'] });
}

// --- Overview ---------------------------------------------------------------

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="org-stat">
      <span className="org-stat-value">{value}</span>
      <span className="org-stat-label">{label}</span>
      {hint ? <span className="rn-muted org-stat-hint">{hint}</span> : null}
    </div>
  );
}

function Overview({ program }: { program: ProgramsNS.ProgramDetail }) {
  const s = program.summary;
  const days = Math.round(
    (new Date(s.windowTo).getTime() - new Date(s.windowFrom).getTime()) / 86_400_000,
  );
  return (
    <>
      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">At a glance</h2>
          <span className="rn-muted">Last {days} days</span>
        </div>
        <div className="org-stats">
          <Stat label="Active people" value={String(s.activeWorkers)} />
          {/* The count and the LIST of the same fact, four inches apart, and
              only the list was flagged. `program.supervisors` arrives `[]` with
              `supervisorsWithheld: true` for a reader without `user:read`, and
              the list correctly says "Not shown to you" — while this tile
              printed `Supervisors: 0`, which is the same withholding rendered
              as an absence, in the reassuring direction, in larger type.
              `ProgramSummary` carries no flag of its own, so the sibling
              collection's is the honest signal available. */}
          <Stat
            label="Supervisors"
            value={program.supervisorsWithheld ? WITHHELD_LABEL : String(s.supervisors)}
            hint={
              program.supervisorsWithheld
                ? withheldSentence(WITHHELD_BECAUSE.workerIdentity)
                : 'Direct supervisors of this program’s people'
            }
          />
          <Stat label="Sites" value={String(s.sites)} />
          <Stat label="Scheduled visits" value={String(s.scheduledVisits)} />
          {s.scheduledShifts > 0 ? (
            <Stat label="Scheduled shift hours" value={s.scheduledHours.toFixed(1)} />
          ) : null}
          <Stat label="Services delivered" value={String(s.servicesDelivered)} />
          <Stat
            label="Hours worked"
            value={s.workedHours === null ? 'Not measured' : s.workedHours.toFixed(1)}
            hint={s.workedHours === null ? 'No time clock connected' : undefined}
          />
          <Stat label="Needs attention" value={String(s.openAttentionItems)} />
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)', fontSize: 13 }}>
          Hours worked counts sessions by people who were assigned to this program{' '}
          <em>on the day they worked</em> — so a person who moved programs last month does not drag
          their old hours with them.
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Who runs it</h2>
        <dl className="org-facts">
          <div>
            <dt>Program lead</dt>
            {/* Never `?? 'Not set'`: for a caller without `user:read` the name
                is null because it is being WITHHELD, and "Not set" would tell
                them this program is unowned. */}
            <dd>{programLeadLine(program)}</dd>
          </div>
          <div>
            <dt>Main site</dt>
            <dd>{program.primarySiteName ?? 'No fixed site'}</dd>
          </div>
          <div>
            <dt>Runs from</dt>
            <dd>{program.startsOn ?? '—'}</dd>
          </div>
          <div>
            <dt>Runs until</dt>
            <dd>{program.endsOn ?? '—'}</dd>
          </div>
        </dl>
        {program.supervisors.length > 0 ? (
          <>
            <p className="rn-muted" style={{ marginTop: 'var(--space-14)' }}>
              Supervisors of people in this program. This is who they report to — it is not the same
              relationship as the program lead above.
            </p>
            <ul className="org-inline-list">
              {program.supervisors.map((s) => (
                <li key={s.workerId}>
                  {s.displayName} <span className="rn-muted">· {s.workerCount} people</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          // An emptied roll-up used to vanish with the genuinely empty one, so a
          // program whose supervisors were withheld read as a program where
          // nobody reports to anybody. `supervisorsLine` is null in the second
          // case, which is the case with nothing to say.
          (() => {
            const line = supervisorsLine(program);
            return line ? (
              <p className="rn-muted" style={{ marginTop: 'var(--space-14)' }} role="note">
                Supervisors of people in this program: {line}
              </p>
            ) : null;
          })()
        )}
      </SettingsCard>

      {/* WHAT PHIBIAN NOTICED IN THIS PROGRAM. A program is an operational
          object here, not a form (see this file's own docblock), and "what is
          going on with the people in it" is the question this tab already
          answers in hours and headcounts. `finding.program_id` is the
          worker's primary program at observation time (`findings/sources.ts`),
          so the filter is the same population the stats above count. */}
      <FindingsPanel scope={{ kind: 'program', programId: program.id, what: program.name }} />
    </>
  );
}

// --- People -----------------------------------------------------------------

function People({
  program,
  canManage,
  onChanged,
}: {
  program: ProgramsNS.ProgramDetail;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { has } = usePermissions();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [makePrimary, setMakePrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Picking people BY NAME needs the directory key, not just the manage key.
   *
   * The lead select below already reasoned this way and declined to call the
   * endpoint without `user:read`; this card did not, and it is the affordance
   * that actually reached the unredacted roster. The API now refuses that call,
   * so offering the control would produce a 403 the person cannot act on.
   */
  const mayPickPeople = canManage && has('user:read');

  const candidates = useQuery({
    queryKey: ['programs', 'assignable', program.id, search],
    queryFn: () => programs.assignableWorkers(program.id, { q: search.trim() || undefined }),
    enabled: adding && mayPickPeople,
  });

  const add = useMutation({
    mutationFn: () =>
      programs.addWorkers(program.id, {
        workerIds: selected,
        makePrimary: makePrimary || undefined,
      }),
    onSuccess: () => {
      setSelected([]);
      setAdding(false);
      setError(null);
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t add those people.'),
  });

  // PH-UX-004. Row-scoped: the trigger lives inside a table column's `render`,
  // so the pending row travels in the state rather than the dialog being
  // rendered once per row. Holding the WORKER (not just an id) is what lets the
  // consequence name the person being removed.
  const [confirmRemove, setConfirmRemove] = useState<ProgramsNS.ProgramWorker | null>(null);

  const remove = useMutation({
    mutationFn: (workerId: string) => programs.removeWorkers(program.id, { workerIds: [workerId] }),
    onSuccess: onChanged,
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t remove that person.'),
  });

  const current = currentWorkers(program.workers);
  const past = pastWorkers(program.workers);

  // `identityWithheld` nulls five fields at once, and three of them are
  // legitimately null on a full row — so `?? '—'` made a redacted row read as a
  // real one describing somebody with no job title and no supervisor. The
  // relationship columns (primary, since) are the caller's to see and stay.
  const columns: Column<ProgramsNS.ProgramWorker>[] = [
    {
      key: 'name',
      header: 'Person',
      render: (w) =>
        w.identityWithheld ? (
          <span className="rn-muted">{rosterCell(w, w.displayName, '—')}</span>
        ) : (
          w.displayName
        ),
    },
    { key: 'role', header: 'Job title', render: (w) => rosterCell(w, w.roleTitle, '—') },
    {
      key: 'primary',
      header: 'Primary program',
      render: (w) => (w.isPrimary ? <Pill variant="good">Primary</Pill> : <span>—</span>),
    },
    {
      key: 'supervisor',
      header: 'Reports to',
      render: (w) => rosterCell(w, w.supervisorName, '—'),
    },
    { key: 'since', header: 'In program since', render: (w) => w.effectiveFrom },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: 'Actions',
            render: (w: ProgramsNS.ProgramWorker) => (
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={remove.isPending}
                onClick={() => setConfirmRemove(w)}
              >
                Remove
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {/* PH-UX-004: removing somebody from a program closes their assignment —
          it was one click per table row, which is the easiest kind of row to
          hit by mistake. The name is only spoken when identity is NOT withheld
          (AGENTS.md §9 / the withholding contract): a dialog must not become a
          side channel for a name the roster itself is refusing to show. */}
      <ConfirmAction
        open={confirmRemove !== null}
        title={
          confirmRemove !== null && !confirmRemove.identityWithheld
            ? `Remove ${confirmRemove.displayName} from ${program.name}?`
            : `Remove this person from ${program.name}?`
        }
        consequence={
          confirmRemove !== null && !confirmRemove.identityWithheld
            ? `This ends ${confirmRemove.displayName}’s assignment to “${program.name}” as of ` +
              `today. They stay on the roster of any other program they belong to, and their ` +
              `past time in this one is kept on the record. To undo it you would have to add ` +
              `them back, which starts a new assignment rather than restoring this one.`
            : `This ends that person’s assignment to “${program.name}” as of today. They stay ` +
              `on the roster of any other program they belong to, and their past time in this ` +
              `one is kept on the record. To undo it you would have to add them back, which ` +
              `starts a new assignment rather than restoring this one.`
        }
        confirmLabel="Remove from program"
        pendingLabel="Removing…"
        pending={remove.isPending}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          if (confirmRemove !== null) remove.mutate(confirmRemove.workerId);
        }}
      />
      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">In this program now</h2>
          {mayPickPeople ? (
            <button
              type="button"
              className="rn-btn-quiet"
              aria-expanded={adding}
              onClick={() => setAdding((a) => !a)}
            >
              {adding ? 'Cancel' : 'Add people'}
            </button>
          ) : canManage ? (
            // Say why, rather than leaving a manager to wonder where the button
            // went. The fact is not missing; it is not being shown to them.
            <span className="rn-muted">{withheldSentence(WITHHELD_BECAUSE.workerIdentity)}</span>
          ) : null}
        </div>

        {adding && mayPickPeople ? (
          <div className="org-picker">
            <label className="org-filter">
              <span className="rn-muted">Search people</span>
              <input
                style={{ marginBottom: 0 }}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {/* "Everyone matching that search is already in this program" was
                printed from `candidates.data ?? []`, i.e. also when the search
                failed. The reader then stops looking for somebody who is there. */}
            {candidates.isLoading ? (
              <SkeletonSet surface="table" rows={3} />
            ) : candidates.isError && candidates.data === undefined ? (
              <LoadFailure
                error={candidates.error}
                message="We couldn’t search the roster just now, so we cannot say who is available to add."
                onRetry={() => void candidates.refetch()}
              />
            ) : (
              <div className="org-checklist" role="group" aria-label="People to add">
                {(candidates.data ?? [])
                  .filter((c) => !c.inProgram)
                  .map((c) => (
                    <label key={c.workerId} className="org-check">
                      <input
                        type="checkbox"
                        checked={selected.includes(c.workerId)}
                        onChange={(e) =>
                          setSelected(
                            e.target.checked
                              ? [...selected, c.workerId]
                              : selected.filter((id) => id !== c.workerId),
                          )
                        }
                      />
                      <span>{c.displayName}</span>
                      {c.primaryProgramName ? (
                        <span className="rn-muted"> · currently {c.primaryProgramName}</span>
                      ) : null}
                    </label>
                  ))}
                {(candidates.data ?? []).filter((c) => !c.inProgram).length === 0 ? (
                  <p className="rn-muted" style={{ fontSize: 13 }}>
                    Everyone matching that search is already in this program.
                  </p>
                ) : null}
              </div>
            )}
            <label className="org-check">
              <input
                type="checkbox"
                checked={makePrimary}
                onChange={(e) => setMakePrimary(e.target.checked)}
              />
              <span>
                Make this their primary program
                <span className="rn-muted">
                  {' '}
                  — this moves them out of their current primary, and records the move.
                </span>
              </span>
            </label>
            <div className="set-actions">
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={selected.length === 0 || add.isPending}
                onClick={() => add.mutate()}
              >
                {add.isPending
                  ? 'Adding…'
                  : selected.length === 0
                    ? 'Add people'
                    : `Add ${selected.length} ${selected.length === 1 ? 'person' : 'people'}`}
              </button>
            </div>
          </div>
        ) : null}

        {error ? (
          <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
            {error}
          </p>
        ) : null}

        {/* Said once above the table rather than in every cell. The people are
            still COUNTED and still listed — the caller may know the program has
            members, just not who they are. */}
        {current.some((w) => w.identityWithheld) ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }} role="note">
            {withheldSentence(WITHHELD_BECAUSE.workerIdentity)}. These people are in the program;
            their names and job titles are not yours to see.
          </p>
        ) : null}

        {current.length === 0 ? (
          <SuccessEmpty message="Nobody is assigned to this program yet." />
        ) : (
          <TableCard
            columns={columns}
            rows={current}
            rowKey={(w) => w.assignmentId}
            caption="People currently assigned to this program"
          />
        )}
      </SettingsCard>

      {past.length > 0 ? (
        <SettingsCard>
          <h2 className="rn-card-title">Previously in this program</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Keeping these is what lets past work stay attributed to the program the person was
            actually in at the time.
          </p>
          <TableCard
            columns={[
              { key: 'name', header: 'Person', render: (w) => rosterCell(w, w.displayName, '—') },
              { key: 'from', header: 'From', render: (w) => w.effectiveFrom },
              { key: 'to', header: 'Until', render: (w) => w.effectiveTo ?? '—' },
            ]}
            rows={past}
            rowKey={(w) => w.assignmentId}
            caption="Past assignments"
          />
        </SettingsCard>
      ) : null}
    </>
  );
}

// --- Sites ------------------------------------------------------------------

function Sites({
  program,
  canManage,
  onChanged,
}: {
  program: ProgramsNS.ProgramDetail;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [siteIds, setSiteIds] = useState<string[]>(program.sites.map((s) => s.siteId));
  const [primarySiteId, setPrimarySiteId] = useState<string>(
    program.sites.find((s) => s.isPrimary)?.siteId ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSiteIds(program.sites.map((s) => s.siteId));
    setPrimarySiteId(program.sites.find((s) => s.isPrimary)?.siteId ?? '');
  }, [program.sites]);

  const options = useQuery({
    queryKey: ['sites', 'options'],
    queryFn: () => sites.list({ limit: 200, sort: 'name' }),
  });

  const save = useMutation({
    mutationFn: () =>
      programs.setSites(program.id, {
        siteIds,
        primarySiteId: primarySiteId || null,
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t save those sites.'),
  });

  const currentIds = program.sites
    .map((s) => s.siteId)
    .sort()
    .join(',');
  const currentPrimary = program.sites.find((s) => s.isPrimary)?.siteId ?? '';
  const dirty = [...siteIds].sort().join(',') !== currentIds || primarySiteId !== currentPrimary;

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Sites this program operates from</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        A program can run from no fixed site, one, or several. Editing here is the same as editing
        the program list on a site’s own screen — there is one relationship, not two.
      </p>

      {options.isLoading ? (
        <SkeletonSet surface="table" rows={3} />
      ) : options.isError && options.data === undefined ? (
        <LoadFailure
          error={options.error}
          message="We couldn’t load your sites just now. This is not the same as having none."
          onRetry={() => void options.refetch()}
        />
      ) : (options.data?.sites.length ?? 0) === 0 ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          No sites are set up yet. Add one under Sites &amp; locations, or leave this program with
          no fixed site.
        </p>
      ) : (
        <div className="org-checklist" style={{ marginTop: 'var(--space-12)' }}>
          {(options.data?.sites ?? []).map((site) => {
            const checked = siteIds.includes(site.id);
            return (
              <div key={site.id} className="org-site-row">
                <label className="org-check">
                  <input
                    type="checkbox"
                    disabled={!canManage}
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...siteIds, site.id]
                        : siteIds.filter((id) => id !== site.id);
                      setSiteIds(next);
                      if (!next.includes(primarySiteId)) setPrimarySiteId(next[0] ?? '');
                    }}
                  />
                  <span>{site.name}</span>
                  {site.city ? <span className="rn-muted"> · {site.city}</span> : null}
                </label>
                {checked ? (
                  <label className="org-check">
                    <input
                      type="radio"
                      name="primary-site"
                      disabled={!canManage}
                      checked={primarySiteId === site.id}
                      onChange={() => setPrimarySiteId(site.id)}
                    />
                    <span className="rn-muted">Main site</span>
                  </label>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {canManage ? (
        <SettingsActions
          dirty={dirty}
          valid
          saving={save.isPending}
          saved={!dirty && save.isSuccess}
          error={error}
          onSave={() => save.mutate()}
          onCancel={() => {
            setSiteIds(program.sites.map((s) => s.siteId));
            setPrimarySiteId(currentPrimary);
            setError(null);
          }}
          saveLabel="Save sites"
        />
      ) : null}
    </SettingsCard>
  );
}

// --- Settings ---------------------------------------------------------------

function ProgramSettingsTab({
  program,
  canManage,
  onChanged,
  onDeleted,
}: {
  program: ProgramsNS.ProgramDetail;
  canManage: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { has } = usePermissions();
  /**
   * The lead is a member's name, governed by `user:read` like every other
   * identity fact. Without it the server withholds the field, so this person is
   * looking at a select whose current value they were never shown — and whose
   * only visible option, "Not set", is a lie about the program.
   *
   * The control is not offered. `programUpdatePayload` already guarantees an
   * untouched field is not transmitted, and the API refuses the write outright,
   * so this is neither of those things: it is not asking somebody to choose
   * between an unknown current value and a false one.
   */
  const mayEditLead = has('user:read');
  const [draft, setDraft] = useState(() => programDraftOf(program));
  const [error, setError] = useState<string | null>(null);
  const [confirmRetire, setConfirmRetire] = useState(false);
  // PH-UX-004. `confirmRetire` gates RETIRE, which is reversible. The hard
  // DELETE sits in a sibling branch this state never reaches — the same shape
  // as SiteDetail, and the reason a `grep -c confirm` on this file understated
  // the remaining work.
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setDraft(programDraftOf(program));
  }, [program]);

  const leadOptions = useQuery({
    queryKey: ['programs', 'assignable', program.id, ''],
    queryFn: () => programs.assignableWorkers(program.id, { limit: 300 }),
    // Not fetched when the picker is not rendered. That endpoint answers with
    // the assignable workforce BY NAME, which is precisely what this principal
    // is not entitled to — asking for it and then not showing it would be
    // requesting a disclosure the screen has just decided not to make.
    enabled: canManage && mayEditLead,
  });

  /**
   * THE LEAD PICKER IS A WRITE, SO ITS EMPTY STATE DESTROYS DATA.
   *
   * `(leadOptions.data ?? []).map(...)` under `<option value="">Not set</option>`
   * meant a failed, refused or still-pending roster read produced a select
   * offering exactly one choice: "Not set". Choosing it makes the field dirty,
   * `programUpdatePayload` therefore includes `programLeadWorkerId: null`, and
   * the save deletes a lead the administrator never saw a name for.
   *
   * That is the defect `docs/withholding-contract.md` was written around,
   * reached through a failed request rather than through a permission — and
   * `programUpdatePayload`'s diff cannot stop it, because the person really did
   * change the field. The control has to stop being operable when there is
   * nothing behind it.
   */
  const leadPicker = pickerState(
    listPhase(leadOptions, leadOptions.data?.length),
    'assignable people',
    'Not set',
  );

  const save = useMutation({
    // ONLY WHAT CHANGED. This used to send every field unconditionally, which
    // meant an administrator who could not SEE the program lead transmitted
    // `programLeadWorkerId: null` on every save and silently deleted it. See
    // `programUpdatePayload`.
    mutationFn: () => programs.update(program.id, programUpdatePayload(draft, program)),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'We couldn’t save that.'),
  });

  const retire = useMutation({
    mutationFn: () => programs.retire(program.id),
    onSuccess: () => {
      setConfirmRetire(false);
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t retire that program.'),
  });

  const reactivate = useMutation({
    mutationFn: () => programs.reactivate(program.id),
    onSuccess: onChanged,
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t reactivate that program.'),
  });

  const destroy = useMutation({
    mutationFn: () => programs.remove(program.id),
    onSuccess: onDeleted,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'We couldn’t delete that.'),
  });

  const errors = validateProgramDraft(draft);
  // Derived from the payload, so the Save button and the request it sends can
  // never disagree about what changed. They used to: this comparison correctly
  // reported the withheld lead unchanged while the payload sent it anyway.
  const dirty = programDraftDirty(draft, program);
  const editable = canManage && canEdit(program.status);

  return (
    <>
      <SettingsCard>
        <h2 className="rn-card-title">Program details</h2>
        {!canEdit(program.status) ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            This program is retired, so its details are read-only. Reactivate it below to make
            changes.
          </p>
        ) : null}
        <div className="set-fields" style={{ marginTop: 'var(--space-14)' }}>
          <SettingsField label="Name" error={errors['name']}>
            <input
              style={{ marginBottom: 0 }}
              value={draft.name}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Short code" error={errors['code']}>
            <input
              style={{ marginBottom: 0 }}
              value={draft.code}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            />
          </SettingsField>
          <SettingsField
            label="Program lead"
            hint="Who runs this program. This is separate from who each person reports to."
          >
            {mayEditLead ? (
              <>
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.programLeadWorkerId}
                  disabled={!editable || leadPicker.disabled}
                  onChange={(e) => setDraft({ ...draft, programLeadWorkerId: e.target.value })}
                >
                  <option value="">{leadPicker.placeholder}</option>
                  {(leadOptions.data ?? []).map((w) => (
                    <option key={w.workerId} value={w.workerId}>
                      {w.displayName}
                    </option>
                  ))}
                </select>
                {leadPicker.note !== null ? (
                  <p className="rn-muted" style={{ fontSize: 12 }} role="note">
                    {leadPicker.note}
                  </p>
                ) : null}
              </>
            ) : (
              // Read-only, and it SAYS which of the two nulls this is. An
              // editable select here offered a choice between a current value
              // this person was never shown and a "Not set" option that would
              // have been a false statement about the program.
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                {programLeadLine(program)}
              </p>
            )}
          </SettingsField>
          <SettingsField label="Runs from">
            <input
              style={{ marginBottom: 0 }}
              type="date"
              value={draft.startsOn}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, startsOn: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Runs until" error={errors['endsOn']}>
            <input
              style={{ marginBottom: 0 }}
              type="date"
              value={draft.endsOn}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, endsOn: e.target.value })}
            />
          </SettingsField>
        </div>
        <SettingsField label="Description" error={errors['description']}>
          <textarea
            style={{ marginBottom: 0 }}
            rows={3}
            value={draft.description}
            disabled={!editable}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </SettingsField>

        {editable ? (
          <SettingsActions
            dirty={dirty}
            valid={Object.keys(errors).length === 0}
            saving={save.isPending}
            saved={!dirty && save.isSuccess}
            error={error}
            onSave={() => save.mutate()}
            onCancel={() => setDraft(programDraftOf(program))}
          />
        ) : null}
      </SettingsCard>

      {canManage ? (
        <SettingsCard>
          <h2 className="rn-card-title">Lifecycle</h2>
          {program.status === 'retired' ? (
            <>
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                This program is retired. Its history is intact and still reportable; it just isn’t
                offered for new work. Reactivating brings it back — the people who were removed when
                it retired are not restored.
              </p>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={reactivate.isPending}
                onClick={() => reactivate.mutate()}
              >
                {reactivate.isPending ? 'Reactivating…' : 'Reactivate program'}
              </button>
            </>
          ) : (
            <>
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                Retiring takes a program out of service without deleting anything. Past schedules,
                services, documents and reports keep referring to it. Its{' '}
                {currentWorkers(program.workers).length} current{' '}
                {currentWorkers(program.workers).length === 1 ? 'assignment' : 'assignments'} will
                be closed as of today, so this month’s work stays where it belongs.
              </p>
              {confirmRetire ? (
                <div className="set-actions">
                  <button
                    type="button"
                    className="signin-devbtn"
                    style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                    disabled={retire.isPending}
                    onClick={() => retire.mutate()}
                  >
                    {retire.isPending ? 'Retiring…' : 'Yes, retire this program'}
                  </button>
                  <button
                    type="button"
                    className="rn-btn-quiet"
                    onClick={() => setConfirmRetire(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => setConfirmRetire(true)}
                >
                  Retire program
                </button>
              )}
            </>
          )}

          {program.deletable ? (
            <>
              <p className="rn-muted" style={{ marginTop: 'var(--space-16)', fontSize: 13 }}>
                Nothing references this program yet, so it can still be deleted outright.{' '}
                <button
                  type="button"
                  className="rn-btn-quiet"
                  disabled={destroy.isPending}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete permanently
                </button>
              </p>
              {/* Outside the <p>: the dialog renders a div. */}
              <ConfirmAction
                open={confirmDelete}
                title={`Delete ${program.name} permanently?`}
                consequence={
                  `This deletes the program “${program.name}” outright — the row itself, not ` +
                  `just its status. There is no undo and no retired copy to restore from; if ` +
                  `you need it back you will have to create it again from scratch. If you only ` +
                  `want it closed to new work, cancel and use Retire instead.`
                }
                confirmLabel="Delete permanently"
                pendingLabel="Deleting…"
                pending={destroy.isPending}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={() => destroy.mutate()}
              />
            </>
          ) : (
            <p className="rn-muted" style={{ marginTop: 'var(--space-16)', fontSize: 13 }}>
              This program is already referenced by schedules, services or people, so it can no
              longer be deleted — only retired. That is deliberate: deleting it would strand those
              records.
            </p>
          )}
        </SettingsCard>
      ) : null}
    </>
  );
}
